import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { EditorState, Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { sql } from '@codemirror/lang-sql';
import { autocompletion, completionKeymap, startCompletion } from '@codemirror/autocomplete';

@Component({
    selector: 'app-json-text-area',
    templateUrl: './json-text-area.component.html',
    styleUrls: ['./json-text-area.component.scss'],
    standalone: false
})
export class JsonTextAreaComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("editorContainer", { static: true }) editorContainer!: ElementRef;
  @Output() enteredValue = new EventEmitter<any>();
  @Input() dataValue!: any;
  
  private _data: any;
  set data(value: any) {
    this._data = value;
    if (this.editorView && this._data !== undefined) {
      this.updateEditorValue(this._data);
    }
  }
  
  get data(): any {
    return this._data;
  }

  private editorView!: EditorView;
  private textareaElement!: HTMLTextAreaElement;

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    const initialValue = this._data || this.dataValue || '';
    
    // Create extensions
    const extensions: Extension[] = [
      lineNumbers(),
      history(),
      sql(),
      autocompletion(),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...completionKeymap,
        {
          key: 'Ctrl-Space',
          run: startCompletion
        }
      ]),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          const value = update.state.doc.toString();
          this._data = value;
          // Sync with textarea if it exists
          if (this.textareaElement) {
            this.textareaElement.value = value;
          }
          this.enteredValue.emit({ value: this._data });
        }
      }),
      EditorView.theme({
        '&': {
          border: '1px solid var(--dblue)',
        },
        '.cm-content': {
          padding: '10px',
          minHeight: '200px',
        },
        '.cm-focused': {
          outline: 'none',
        }
      })
    ];

    // Create editor state
    const state = EditorState.create({
      doc: initialValue,
      extensions: extensions
    });

    // Create editor view
    this.editorView = new EditorView({
      state: state,
      parent: this.editorContainer.nativeElement
    });

    // Create hidden textarea for form compatibility
    this.textareaElement = document.createElement('textarea');
    this.textareaElement.style.display = 'none';
    this.textareaElement.value = initialValue;
    this.editorContainer.nativeElement.appendChild(this.textareaElement);
  }

  private updateEditorValue(value: string): void {
    if (this.editorView) {
      const transaction = this.editorView.state.update({
        changes: {
          from: 0,
          to: this.editorView.state.doc.length,
          insert: value
        }
      });
      this.editorView.dispatch(transaction);
      if (this.textareaElement) {
        this.textareaElement.value = value;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.editorView) {
      this.editorView.destroy();
    }
  }
}

